/**
 * CLIENT TWIN of functions/src/billing/burnRate.ts — keep them identical.
 *
 * The wallet page has to show the SAME suggested amounts the server would
 * arm, or the number a person clicks is not the number they get charged. That
 * is the exact class of surprise INCIDENT 2026-09-10 was about, so the two
 * copies are pinned against one shared table by
 * tests/wallet/burnRateParity.test.ts (the messageAuthorship.ts client-twin
 * idiom).
 *
 * If you change one, change the other in the same commit.
 */

/** One day's total spend, as read from the expense ledger. */
export interface DailySpend {
  /** UTC day, `YYYY-MM-DD`. */
  day: string;
  usd: number;
}

export interface BurnRate {
  /** The blended daily burn used for every suggestion below. */
  dailyUsd: number;
  /** The inputs, surfaced so the UI can explain itself and tests can assert. */
  shortTermUsd: number;
  mediumTermUsd: number;
  /** Days of real data behind the estimate. */
  observedDays: number;
  /** False when there is too little history to personalise anything. */
  confident: boolean;
}

/** Windows, in days, for the two averages we blend. */
export const SHORT_WINDOW_DAYS = 7;
export const MEDIUM_WINDOW_DAYS = 30;
/** Below this many observed days we do not claim to know the user's pace. */
export const MIN_OBSERVED_DAYS = 3;
/** Recent pace carries more weight than the long baseline, but not all of it. */
export const SHORT_WEIGHT = 0.6;
/** Daily totals above this percentile are pulled down before averaging. */
export const WINSOR_PERCENTILE = 0.9;

/**
 * Clamp the fiercest days down to the 90th percentile before averaging.
 *
 * A single deep-research burst can be 50x a normal day. Letting it through
 * means one unusual afternoon permanently inflates a RECURRING card charge —
 * the surprise this module exists to prevent. Winsorizing keeps the day in the
 * sample (it is real spending) without letting it set the rate.
 */
export function winsorize(values: number[], percentile = WINSOR_PERCENTILE): number[] {
  if (values.length < 4) return values.slice();
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor(percentile * (sorted.length - 1)));
  const cap = sorted[idx];
  return values.map((v) => Math.min(v, cap));
}

/** Exponentially weighted mean — recent days matter more. */
export function exponentialMean(values: number[], alpha = 0.3): number {
  if (values.length === 0) return 0;
  // `values` arrives oldest-first; seed on the oldest and walk forward.
  let acc = values[0];
  for (let i = 1; i < values.length; i += 1) acc = alpha * values[i] + (1 - alpha) * acc;
  return acc;
}

export function arithmeticMean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * Blend a short exponentially-weighted average with a longer flat one.
 *
 * Neither alone is honest. The 7-day average alone over-reacts to a quiet week
 * (under-sizing the top-up, so work stops sooner than the user expects) and the
 * 30-day average alone lags a genuine ramp-up (under-sizing it differently).
 * `days` must arrive OLDEST-FIRST and be gap-filled with zeros — a day with no
 * spend is a real zero, not missing data, and dropping it would overstate the
 * burn of anyone who works weekdays only.
 */
export function computeBurnRate(days: DailySpend[]): BurnRate {
  const ordered = [...days].sort((a, b) => a.day.localeCompare(b.day));
  const observedDays = ordered.length;

  const takeLast = (n: number) => ordered.slice(Math.max(0, ordered.length - n)).map((d) => d.usd);
  const shortTermUsd = exponentialMean(winsorize(takeLast(SHORT_WINDOW_DAYS)));
  const mediumTermUsd = arithmeticMean(winsorize(takeLast(MEDIUM_WINDOW_DAYS)));

  const blended =
    mediumTermUsd > 0
      ? SHORT_WEIGHT * shortTermUsd + (1 - SHORT_WEIGHT) * mediumTermUsd
      : shortTermUsd;

  return {
    dailyUsd: Number.isFinite(blended) && blended > 0 ? blended : 0,
    shortTermUsd,
    mediumTermUsd,
    observedDays,
    confident: observedDays >= MIN_OBSERVED_DAYS && blended > 0,
  };
}

// ---------------------------------------------------------------------------
// Suggested amounts
// ---------------------------------------------------------------------------

/** The four horizons Miles named. */
export const SUGGESTION_HORIZONS: ReadonlyArray<{ days: number; label: string }> = [
  { days: 3, label: 'a few days' },
  { days: 7, label: 'about a week' },
  { days: 14, label: 'about two weeks' },
  { days: 30, label: 'about a month' },
];

/**
 * Describe a span of runway in the words a person would use.
 *
 * Derived from the MONEY, never from the horizon we aimed at. Rounding and the
 * platform cap both move the real figure: a heavy account's "about a month"
 * chip lands on the $1,000 ceiling, which at $43/day is 23 days, not 30.
 * Labelling that "about a month" would be a small lie on the one control this
 * whole incident was about, so the label follows what the amount actually buys.
 */
export function describeRunway(days: number): string {
  if (!Number.isFinite(days) || days <= 0) return '';
  if (days <= 4) return 'a few days';
  if (days <= 10) return 'about a week';
  if (days <= 18) return 'about two weeks';
  if (days <= 45) return 'about a month';
  return `about ${Math.round(days / 30)} months`;
}

/** Auto top-up targets this many days of burn (Miles, 2026-09-11: "5-7 days"). */
export const AUTO_TOP_UP_TARGET_DAYS = 6;
/** Auto top-up fires when the balance falls under this many days of runway. */
export const AUTO_TOP_UP_THRESHOLD_DAYS = 2;

/** Platform floors/ceilings. A suggestion never leaves this range. */
export const MIN_SUGGESTION_USD = 25;
export const MAX_SUGGESTION_USD = 1000;
/** Auto top-up is capped tighter than a manual one — it happens unattended. */
export const MAX_AUTO_TOP_UP_USD = 500;
export const MIN_AUTO_TOP_UP_USD = 50;
export const MIN_AUTO_TOP_UP_THRESHOLD_USD = 25;

/**
 * Round to something a person reads as a price rather than a measurement.
 * $137.42 becomes $150; $612 becomes $600.
 */
export function roundToLegibleAmount(usd: number): number {
  if (!Number.isFinite(usd) || usd <= 0) return 0;
  if (usd < 100) return Math.max(5, Math.round(usd / 5) * 5);
  if (usd < 500) return Math.round(usd / 25) * 25;
  return Math.round(usd / 50) * 50;
}

export interface AmountSuggestion {
  usd: number;
  /** Days of runway this actually buys at the current pace. */
  days: number;
  /** "about a week" — the horizon it was derived from. */
  horizonLabel: string;
  /** Ready-to-render sublabel, e.g. "about a week at your pace". */
  subLabel: string;
}

/**
 * The personalised ladder. Falls back to the flat ladder when we genuinely
 * cannot estimate — a brand-new organisation has no pace, and inventing one
 * would be worse than showing round numbers.
 */
export const FALLBACK_LADDER_USD: readonly number[] = [50, 100, 250, 500];

export function suggestTopUpAmounts(burn: BurnRate): AmountSuggestion[] {
  if (!burn.confident || burn.dailyUsd <= 0) {
    return FALLBACK_LADDER_USD.map((usd) => ({
      usd,
      days: 0,
      horizonLabel: '',
      subLabel: '',
    }));
  }

  const seen = new Set<number>();
  const out: AmountSuggestion[] = [];
  for (const horizon of SUGGESTION_HORIZONS) {
    const raw = burn.dailyUsd * horizon.days;
    const usd = Math.min(
      MAX_SUGGESTION_USD,
      Math.max(MIN_SUGGESTION_USD, roundToLegibleAmount(raw)),
    );
    // Two horizons can collapse onto the same rounded figure (or onto the cap)
    // for very light or very heavy accounts. Showing "$500" twice reads as a
    // bug, so drop the duplicate rather than pad the row.
    if (seen.has(usd)) continue;
    seen.add(usd);
    const days = Math.round(usd / burn.dailyUsd);
    out.push({
      usd,
      days,
      horizonLabel: horizon.label,
      // From the money, not the intent — see describeRunway.
      subLabel: `${describeRunway(days)} at your pace`,
    });
  }
  return out;
}

export interface AutoTopUpSuggestion {
  topUpAmountUsd: number;
  thresholdUsd: number;
  /** Null when we are falling back rather than personalising. */
  dailyUsd: number | null;
  personalised: boolean;
}

/**
 * The auto top-up defaults this account should be OFFERED.
 *
 * Only ever a suggestion: the amount a user last CONFIRMED is a ceiling the
 * server will not exceed on its own (see autoTopUp.ts). A silently
 * recomputing charge would rebuild the original surprise in a new place.
 */
export function suggestAutoTopUp(
  burn: BurnRate,
  fallback: { topUpAmountUsd: number; thresholdUsd: number },
): AutoTopUpSuggestion {
  if (!burn.confident || burn.dailyUsd <= 0) {
    return { ...fallback, dailyUsd: null, personalised: false };
  }
  const amount = Math.min(
    MAX_AUTO_TOP_UP_USD,
    Math.max(MIN_AUTO_TOP_UP_USD, roundToLegibleAmount(burn.dailyUsd * AUTO_TOP_UP_TARGET_DAYS)),
  );
  // The threshold buys time to react — to a decline, to an SCA challenge, or
  // simply to notice. At $25 with a $50/day burn the user had twelve hours.
  const threshold = Math.max(
    MIN_AUTO_TOP_UP_THRESHOLD_USD,
    roundToLegibleAmount(burn.dailyUsd * AUTO_TOP_UP_THRESHOLD_DAYS),
  );
  return {
    topUpAmountUsd: amount,
    // Never let the trigger point exceed the top-up itself, which would make
    // every charge instantly re-trigger the next one.
    thresholdUsd: Math.min(threshold, Math.round(amount / 2)),
    dailyUsd: burn.dailyUsd,
    personalised: true,
  };
}

// ---------------------------------------------------------------------------
// When does the next automatic charge land?
// ---------------------------------------------------------------------------

/**
 * Days until the balance falls to the auto top-up threshold, at this pace.
 *
 * Shared because BOTH halves need it and must agree: the wallet card renders
 * "next top-up ≈ <date>" from it, and the pre-charge warning decides when to
 * say "we'll add $X around <date>" from the same number. Two implementations
 * would eventually tell a customer two different dates for one charge.
 *
 * Null when auto top-up is off, the pace is unknown, or the balance is ALREADY
 * under the threshold — that charge is imminent rather than scheduled, and
 * "in 0 days" reads as a countdown that has already expired.
 */
export function daysUntilNextAutoTopUp(input: {
  enabled: boolean;
  balanceUsd: number;
  thresholdUsd: number;
  burn: BurnRate | null;
}): number | null {
  const { enabled, balanceUsd, thresholdUsd, burn } = input;
  if (!enabled || !burn || !burn.confident || burn.dailyUsd <= 0) return null;
  const runway = balanceUsd - thresholdUsd;
  if (!(runway > 0)) return null;
  return Math.max(1, Math.round(runway / burn.dailyUsd));
}

// ---------------------------------------------------------------------------
// Reading the rollups
// ---------------------------------------------------------------------------

/** Days of history to pull. One more than the medium window, for gap-filling. */
export const DAILY_SPEND_WINDOW_DAYS = MEDIUM_WINDOW_DAYS + 1;

/** `YYYY-MM-DD` keys for the window ending on `nowMs`, NEWEST FIRST. */
export function dailySpendWindowKeys(
  nowMs: number,
  windowDays: number = DAILY_SPEND_WINDOW_DAYS,
): string[] {
  const keys: string[] = [];
  for (let i = 0; i < windowDays; i += 1) {
    const d = new Date(nowMs);
    d.setUTCDate(d.getUTCDate() - i);
    keys.push(d.toISOString().slice(0, 10));
  }
  return keys;
}

/** One `accounts/{id}/account_usage` document, as either side reads it. */
export interface UsageRollupRow {
  id: string;
  amountUsd: unknown;
}

/**
 * Turn raw rollup rows into the gap-filled, oldest-first series
 * `computeBurnRate` expects.
 *
 * Two rules, both load-bearing:
 *
 * The collection holds MONTHLY rollups keyed `YYYY-MM` beside the daily
 * `YYYY-MM-DD` ones, and `2026-09` sorts INSIDE a document-id range that spans
 * September — so the current month's running total arrives in the middle of the
 * window and reads as one colossal day. Every suggested amount, and the date on
 * the pre-charge warning, would inflate. Only a full date shape is kept.
 *
 * A day with no row is a real zero, not missing data: dropping it would
 * overstate the pace of anyone who works weekdays only.
 *
 * This lives in the shared engine rather than in either caller because the
 * wallet card, the suggested amounts and the pre-charge warning must read the
 * SAME series. Two implementations would eventually tell a customer two
 * different dates for one charge.
 */
export function selectDailySpendRows(
  rows: UsageRollupRow[],
  dayKeysNewestFirst: string[],
): DailySpend[] {
  const byDay: Record<string, number> = {};
  for (const row of rows) {
    const id = String(row.id);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(id)) continue;
    const usd = row.amountUsd;
    if (typeof usd === 'number' && Number.isFinite(usd) && usd >= 0) byDay[id] = usd;
  }
  return dayKeysNewestFirst
    .slice()
    .reverse()
    .map((day) => ({ day, usd: byDay[day] ?? 0 }));
}

/**
 * The account whose pace we measure: the one the user is acting in, read from
 * `users/{uid}/account_membership/main` — the same pointer the wallet display
 * resolver uses. Empty string for a personal-plane user, who has no
 * organization pace to personalise against.
 *
 * Here rather than inline in each caller for the same reason as everything else
 * in this file: if the wallet card and the pre-charge warning resolved different
 * accounts they would quote different paces, and the customer would be told two
 * different dates for one charge.
 */
export function accountIdFromMembership(data: unknown): string {
  const member = (data ?? {}) as Record<string, unknown>;
  if (typeof member.accountId === 'string' && member.accountId) return member.accountId;
  const explicit = member.explicitChoice as Record<string, unknown> | null | undefined;
  if (explicit && typeof explicit.accountId === 'string' && explicit.accountId) {
    return explicit.accountId;
  }
  return '';
}
